import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Camera, User as UserIcon, Cloud, CheckCircle2, BellRing, Trophy, ShieldCheck, Copy, RefreshCw } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { BADGE_ICONS } from '../components/UserBadges';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyBadgesCard = ({ user }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/users/${user.id}/badges`).then((r) => setData(r.data)).catch(() => {});
  }, [user?.id]);

  if (!data) return null;
  const earned = new Set(data.badges);
  return (
    <Card className="dashboard-card" data-testid="my-badges-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#b34e00]" />
          My Badges
          <span className="text-sm font-normal text-[#5C5C5C]">({earned.size} of {data.badge_definitions.length} earned)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {data.badge_definitions.map((def) => {
            const Icon = BADGE_ICONS[def.icon] || Trophy;
            const has = earned.has(def.id);
            return (
              <div
                key={def.id}
                title={def.description}
                className={`flex flex-col items-center text-center gap-1.5 rounded-xl border p-3 transition-all ${
                  has ? 'border-transparent shadow-sm' : 'border-dashed border-slate-200 opacity-45 grayscale'
                }`}
                style={has ? { backgroundColor: `${def.color}14` } : {}}
                data-testid={`profile-badge-${def.id}`}
              >
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: has ? def.color : '#cbd5e1' }}>
                  <Icon className="w-5 h-5 text-white" />
                </span>
                <p className="text-xs font-semibold text-[#1A1A1A] leading-tight">{def.name}</p>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[#5C5C5C] mt-4">
          Earn badges by posting, commenting, and giving likes in the Community Forum.
        </p>
      </CardContent>
    </Card>
  );
};

const NotificationPrefsCard = ({ user }) => {
  const [prefs, setPrefs] = useState({ curriculum: true, forum: true, email_digest: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/users/${user.id}/notification-prefs`)
      .then((res) => { setPrefs(res.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [user?.id]);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await axios.put(`${API}/users/${user.id}/notification-prefs`, next);
      const labels = { curriculum: 'Curriculum', forum: 'Forum', email_digest: 'Weekly email digest' };
      toast.success(`${labels[key]} notifications ${next[key] ? 'on' : 'off'}`);
    } catch (e) {
      setPrefs(prefs);
      toast.error('Failed to update notifications');
    }
  };

  return (
    <Card className="dashboard-card" data-testid="notification-prefs-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
          <BellRing className="w-5 h-5 text-[#34657f]" />
          Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className={`space-y-4 transition-opacity ${loaded ? '' : 'opacity-50 pointer-events-none'}`}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">Curriculum updates</p>
            <p className="text-xs text-[#5C5C5C]">New files and resources added to the library.</p>
          </div>
          <Switch checked={prefs.curriculum} disabled={!loaded} onCheckedChange={() => toggle('curriculum')} aria-label="Curriculum update notifications" data-testid="notify-curriculum-switch" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">Forum activity</p>
            <p className="text-xs text-[#5C5C5C]">Posts and comments in threads you follow.</p>
          </div>
          <Switch checked={prefs.forum} disabled={!loaded} onCheckedChange={() => toggle('forum')} aria-label="Forum activity notifications" data-testid="notify-forum-switch" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">Weekly email digest <span className="ml-1 text-[10px] font-bold uppercase tracking-wide text-[#b34e00]">Coming soon</span></p>
            <p className="text-xs text-[#5C5C5C]">A Friday-afternoon email summary of new files and forum activity.</p>
          </div>
          <Switch checked={prefs.email_digest} disabled={!loaded} onCheckedChange={() => toggle('email_digest')} aria-label="Weekly email digest" data-testid="notify-email-digest-switch" />
        </div>
      </CardContent>
    </Card>
  );
};

const DriveConnectionCard = ({ user }) => {
  const [status, setStatus] = useState({ loading: true, connected: false, google_email: null });
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/oauth/drive/status`, { params: { requester_id: user?.id } });
      setStatus({ loading: false, connected: res.data.connected, google_email: res.data.google_email });
    } catch (e) {
      setStatus({ loading: false, connected: false, google_email: null });
    }
  };

  useEffect(() => { if (user?.id) fetchStatus(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    setBusy(true);
    try {
      const a = await axios.get(`${API}/oauth/drive/authorize`, { params: { requester_id: user?.id } });
      const popup = window.open(a.data.authorize_url, 'drive-connect', 'width=520,height=660');
      const onMsg = (ev) => { if (ev.data === 'drive-connected') { window.removeEventListener('message', onMsg); fetchStatus(); setBusy(false); } };
      window.addEventListener('message', onMsg);
      const iv = setInterval(() => {
        if (popup && popup.closed) { clearInterval(iv); window.removeEventListener('message', onMsg); fetchStatus(); setBusy(false); }
      }, 1200);
    } catch (e) {
      toast.error('Google Drive connection is not configured.');
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/oauth/drive/disconnect`, null, { params: { requester_id: user?.id } });
      toast.success('Google Drive disconnected');
      setStatus({ loading: false, connected: false, google_email: null });
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="dashboard-card" data-testid="drive-connection-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
          <Cloud className="w-5 h-5 text-[#34657f]" />
          Google Drive
        </CardTitle>
      </CardHeader>
      <CardContent>
        {status.loading ? (
          <p className="text-sm text-[#5C5C5C]">Checking connection…</p>
        ) : status.connected ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-[#1A1A1A] text-sm" data-testid="drive-connected-email">
                  {status.google_email ? `Connected as ${status.google_email}` : 'Connected to Google Drive'}
                </p>
                <p className="text-xs text-[#5C5C5C]">Used for "Make a copy in my Drive".</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={busy} data-testid="drive-disconnect-btn">
              Disconnect
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-[#1A1A1A] font-medium">Not connected</p>
              <p className="text-xs text-[#5C5C5C]">Connect to copy curriculum files straight into your own Drive.</p>
            </div>
            <Button size="sm" className="bg-[#34657f] hover:bg-[#2a5060]" onClick={handleConnect} disabled={busy} data-testid="drive-connect-btn">
              {busy ? 'Waiting…' : 'Connect Google Drive'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DriveAdminCard = ({ user }) => {
  const [info, setInfo] = useState(null);
  const [sync, setSync] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/oauth/drive/redirect-uri`).then((r) => setInfo(r.data)).catch(() => {});
    axios.get(`${API}/drive/auto-sync-status`).then((r) => setSync(r.data)).catch(() => {});
  }, []);

  const copyUri = () => {
    navigator.clipboard.writeText(info.redirect_uri);
    toast.success('Redirect URI copied — paste it into Google Cloud Console');
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/drive/auto-sync-run`);
      setSync(r.data);
      const s = r.data.stats || {};
      toast.success(`Sync complete — ${s.files_added || 0} new file${s.files_added === 1 ? '' : 's'}, ${s.files_updated || 0} updated`);
    } catch (e) {
      toast.error('Sync failed — check Drive connection');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="dashboard-card" data-testid="drive-admin-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#b34e00]" />
          Drive Admin Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="text-sm font-medium text-[#1A1A1A]">Google OAuth redirect URI (for this domain)</p>
          <p className="text-xs text-[#5C5C5C] mb-2">
            If users see "Error 400: redirect_uri_mismatch", add this exact URL under Authorized redirect URIs in
            Google Cloud Console → APIs &amp; Services → Credentials → your OAuth Client ID.
          </p>
          {info ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-xs bg-slate-100 border border-slate-200 rounded-md px-2.5 py-2" data-testid="oauth-redirect-uri">
                {info.redirect_uri}
              </code>
              <Button variant="outline" size="sm" onClick={copyUri} data-testid="copy-redirect-uri-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>
          ) : (
            <p className="text-xs text-[#5C5C5C]">Loading…</p>
          )}
        </div>
        <div className="border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-[#1A1A1A] flex items-center gap-1.5">
                <RefreshCw className="w-4 h-4 text-[#34657f]" /> Nightly auto-sync
              </p>
              <p className="text-xs text-[#5C5C5C]">
                Every night at 2:00 AM Eastern, the portal checks Google Drive for new or updated files automatically.
              </p>
              <p className="text-xs text-[#5C5C5C] mt-1" data-testid="auto-sync-last-run">
                {sync?.last_run
                  ? `Last run: ${new Date(sync.last_run).toLocaleString()} — ${sync.stats?.files_added ?? 0} added, ${sync.stats?.files_updated ?? 0} updated`
                  : 'Has not run yet.'}
              </p>
            </div>
            <Button size="sm" className="bg-[#34657f] hover:bg-[#2a5060]" onClick={runNow} disabled={running} data-testid="auto-sync-run-btn">
              {running ? 'Syncing…' : 'Run now'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    job_role: user?.job_role || '',
    school_district: user?.school_district || '',
  });

  const mediaSrc = (url) => (!url ? url : (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`));
  const initials = (user?.name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/users/${user.id}/profile`, form, { params: { user_role: user?.role || 'instructor', requester_id: user?.id } });
      updateUser({
        first_name: res.data.first_name,
        last_name: res.data.last_name,
        job_role: res.data.job_role,
        school_district: res.data.school_district,
        name: res.data.name,
      });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('user_role', user?.role || 'instructor');
      fd.append('requester_id', user?.id || '');
      const res = await axios.post(`${API}/users/${user.id}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser({ avatar_url: res.data.avatar_url });
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">My Profile</h1>
        <p className="text-[#5C5C5C] mt-1">Update your details and profile picture.</p>
      </div>

      <Card className="dashboard-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-[#34657f]" />
            Account details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-5">
            <div className="relative group">
              <Avatar className="h-20 w-20">
                <AvatarImage src={mediaSrc(user?.avatar_url)} alt={user?.name} />
                <AvatarFallback className="bg-[#34657f] text-white text-xl font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} data-testid="avatar-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#b34e00] text-white flex items-center justify-center shadow-md hover:bg-[#a84d09] transition-colors"
                title="Change photo"
                data-testid="change-avatar-btn"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div>
              <p className="font-semibold text-[#1A1A1A]">{user?.name}</p>
              <p className="text-sm text-[#5C5C5C]">{user?.email}</p>
              <p className="text-xs text-[#5C5C5C] mt-1">{uploading ? 'Uploading…' : 'Click the camera to add a profile picture'}</p>
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First name</Label>
              <Input id="first_name" value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} data-testid="profile-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last name</Label>
              <Input id="last_name" value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} data-testid="profile-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="job_role">Job role / title</Label>
              <Input id="job_role" value={form.job_role} onChange={(e) => setField('job_role', e.target.value)} placeholder="e.g. Job Coach" data-testid="profile-job-role" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="school_district">School district</Label>
              <Input id="school_district" value={form.school_district} onChange={(e) => setField('school_district', e.target.value)} placeholder="e.g. Lakota Local" data-testid="profile-school-district" />
            </div>
          </div>

          <div className="flex justify-end">
            <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={handleSave} disabled={saving} data-testid="profile-save-btn">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <MyBadgesCard user={user} />
      <NotificationPrefsCard user={user} />
      <DriveConnectionCard user={user} />
      {['system_admin', 'team_admin'].includes(user?.role) && <DriveAdminCard user={user} />}
    </div>
  );
};

export default ProfilePage;
